/**
 * TaskMonitor — page (CES standard)
 * Capacity task monitor: filters by Outpost and status (URL-driven),
 * polling via useCapacityTasks, expandable TaskDetail organism, and
 * cancellation.
 */

import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/atoms/Button';
import { Select } from '../components/atoms/Select';
import { StatusBadge } from '../components/atoms/StatusBadge';
import { TaskDetail } from '../components/organisms/TaskDetail';
import { TASK_RETENTION_MONTHS, useCapacityTasks } from '../hooks/useCapacityTasks';
import { useOutposts } from '../hooks/useOutposts';
import { CANCELLABLE_STATUSES } from '../types/outposts';

/** "Jul 30, 2026, 10:15 AM" in the browser locale; em dash when unknown. */
function formatCreationDate(iso?: string): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

const ALL_STATUSES = [
  'REQUESTED',
  'IN_PROGRESS',
  'WAITING_FOR_EVACUATION',
  'COMPLETED',
  'FAILED',
  'CANCELLATION_IN_PROGRESS',
  'CANCELLED',
];

export function TaskMonitor() {
  const [searchParams, setSearchParams] = useSearchParams();
  const outpostFilter = searchParams.get('outpostId') ?? '';
  const statusFilter = searchParams.get('status') ?? '';

  const { outposts } = useOutposts();
  const {
    tasks,
    loading,
    error,
    cancel,
    cancelError,
    cancellingIds,
    reload,
    refreshTask,
    refreshingIds,
  } = useCapacityTasks({
    outpostId: outpostFilter || undefined,
    status: statusFilter || undefined,
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const setFilter = (key: 'outpostId' | 'status', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <h2 className="mb-1 text-2xl font-bold text-gray-900">Capacity tasks</h2>
      <p className="mb-1 text-sm text-gray-500">
        Slotting changes in flight and their history, refreshed every few seconds
      </p>
      <p className="mb-6 text-xs text-gray-400" data-testid="retention-note">
        Showing tasks created in the last {TASK_RETENTION_MONTHS} months, newest first
      </p>

      <div className="mb-5 flex flex-wrap gap-4">
        <label className="flex min-w-[220px] flex-col gap-1 text-xs text-gray-500">
          Outpost
          <Select
            data-testid="filter-outpost"
            value={outpostFilter}
            onChange={(e) => setFilter('outpostId', e.target.value)}
          >
            <option value="">All outposts</option>
            {outposts.map((outpost) => (
              <option key={outpost.outpostId} value={outpost.outpostId}>
                {outpost.name} ({outpost.outpostId})
              </option>
            ))}
          </Select>
        </label>
        <label className="flex min-w-[220px] flex-col gap-1 text-xs text-gray-500">
          Status
          <Select
            data-testid="filter-status"
            value={statusFilter}
            onChange={(e) => setFilter('status', e.target.value)}
          >
            <option value="">All statuses</option>
            {ALL_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>
        </label>
        <div className="ml-auto self-end">
          <Button variant="secondary" small data-testid="refresh-tasks" onClick={reload}>
            <span className="flex items-center gap-1.5">
              <RefreshCw size={14} aria-hidden="true" />
              Refresh
            </span>
          </Button>
        </div>
      </div>

      {error && <p className="py-8 text-center text-red-600">{error}</p>}
      {cancelError && (
        <p className="py-8 text-center text-red-600" data-testid="cancel-error">
          {cancelError}
        </p>
      )}

      {loading && tasks.length === 0 && !error ? (
        <p className="py-8 text-center text-gray-500">Loading tasks…</p>
      ) : tasks.length === 0 && !error ? (
        <p className="py-8 text-center text-gray-500" data-testid="no-tasks">
          No capacity tasks match the current filters
        </p>
      ) : (
        <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
          {tasks.map((task) => {
            const expanded = expandedId === task.capacityTaskId;
            const cancellable = (CANCELLABLE_STATUSES as readonly string[]).includes(task.status);
            return (
              <div
                key={task.capacityTaskId}
                className="relative"
                data-testid={`task-${task.capacityTaskId}`}
              >
                <button
                  type="button"
                  className="grid w-full cursor-pointer grid-cols-[220px_1fr_170px_auto] items-center gap-4 px-5 py-3.5 pr-[170px] text-left transition-colors hover:bg-gray-50"
                  aria-expanded={expanded}
                  data-testid={`expand-${task.capacityTaskId}`}
                  onClick={() => setExpandedId(expanded ? null : task.capacityTaskId)}
                >
                  <span className="font-mono text-sm font-bold text-gray-900">
                    {task.capacityTaskId}
                  </span>
                  <span className="text-sm text-gray-500">
                    {task.outpostId}
                    {task.assetId ? ` · asset ${task.assetId}` : ' · outpost scope'}
                  </span>
                  <span
                    className="text-sm text-gray-500"
                    data-testid={`created-${task.capacityTaskId}`}
                  >
                    {formatCreationDate(task.creationDate)}
                  </span>
                  <span data-testid={`status-${task.capacityTaskId}`}>
                    <StatusBadge state={task.status} />
                  </span>
                </button>
                <div className="absolute right-4 top-2.5 flex items-center gap-2">
                  {cancellable && (
                    <Button
                      variant="secondary"
                      small
                      data-testid={`cancel-${task.capacityTaskId}`}
                      onClick={() => cancel(task)}
                      disabled={cancellingIds.has(task.capacityTaskId)}
                    >
                      Cancel
                    </Button>
                  )}
                  <button
                    type="button"
                    className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-orange-50 hover:text-orange-600 disabled:cursor-default disabled:opacity-50"
                    aria-label={`Refresh task ${task.capacityTaskId}`}
                    title="Refresh this task"
                    data-testid={`refresh-${task.capacityTaskId}`}
                    onClick={() => refreshTask(task)}
                    disabled={refreshingIds.has(task.capacityTaskId)}
                  >
                    <RefreshCw
                      size={15}
                      aria-hidden="true"
                      className={refreshingIds.has(task.capacityTaskId) ? 'animate-spin' : ''}
                    />
                  </button>
                </div>
                {expanded && <TaskDetail outpostId={task.outpostId} taskId={task.capacityTaskId} />}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
